import { useEffect, useRef } from "react";

import { offset } from "@floating-ui/dom";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useUnifiedCopilotDisplay } from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";

export const UNIFIED_COPILOT_WINDOW_ID = "unified-copilot";

/**
 * The anchor div stays mounted while the window is open even though the button
 * does not: the window resolves its position from this ref after it
 * mounts, so the anchor has to be kept.
 */
export const UnifiedCopilotLauncher: React.FC = () => {
  const { open: openFloatingWindow, close: closeFloatingWindow } =
    useFloatingWindowsActions();
  const isOpen = useIsFloatingWindowOpen(UNIFIED_COPILOT_WINDOW_ID);
  const { surface, open: openCopilot } = useUnifiedCopilotDisplay();
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (surface === "floating" && !isOpen) {
      openFloatingWindow({
        id: UNIFIED_COPILOT_WINDOW_ID,
        type: "unified-copilot",
        target: anchorRef,
        positionOptions: {
          // Above the launcher, right edges aligned, so the window fills the
          // bottom-right region of the viewport.
          placement: "top-end",
          middleware: [offset({ mainAxis: 8 })],
        },
      });
      return;
    }

    if (surface !== "floating" && isOpen) {
      closeFloatingWindow(UNIFIED_COPILOT_WINDOW_ID);
    }
  }, [closeFloatingWindow, isOpen, openFloatingWindow, surface]);

  return (
    <div
      ref={anchorRef}
      className="pointer-events-none fixed right-2.5 bottom-5.5 z-50 h-8 w-8"
      data-loc="unified-copilot-launcher-anchor"
    >
      {surface === "hidden" && (
        <Tooltip placement="left" title="Copilot" asChild>
          <button
            aria-label="Open Copilot"
            className="pointer-events-auto flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:scale-105"
            data-loc="unified-copilot-launcher"
            type="button"
            onClick={openCopilot}
          >
            <Icon icon={faSparkles} size="sm" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};
